import { create } from "zustand";
import { devtools } from "zustand/middleware";

interface HolidayUIState {
  // View state
  activeTab: "future" | "archive";
  selectedHolidayIds: string[];

  // Dialog states
  isCreateDialogOpen: boolean;
  isEditDialogOpen: boolean;
  isDeleteDialogOpen: boolean;
  isBulkDeleteDialogOpen: boolean;
  editingHolidayId: string | null;
  duplicatingHolidayId: string | null;
  deletingHolidayId: string | null;

  // Actions
  setActiveTab: (tab: "future" | "archive") => void;
  setSelectedHolidayIds: (ids: string[]) => void;
  toggleHolidaySelection: (id: string) => void;
  clearSelection: () => void;
  selectAll: (ids: string[]) => void;
  isAllSelected: (ids: string[]) => boolean;

  // Dialog actions
  openCreateDialog: () => void;
  openDuplicateDialog: (holidayId: string) => void;
  closeCreateDialog: () => void;
  openEditDialog: (holidayId: string) => void;
  closeEditDialog: () => void;
  openDeleteDialog: (holidayId: string) => void;
  closeDeleteDialog: () => void;
  openBulkDeleteDialog: () => void;
  closeBulkDeleteDialog: () => void;
  closeAllDialogs: () => void;
}

export const useHolidayUIStore = create<HolidayUIState>()(
  devtools(
    (set, get) => ({
      // Initial state
      activeTab: "future",
      selectedHolidayIds: [],
      isCreateDialogOpen: false,
      isEditDialogOpen: false,
      isDeleteDialogOpen: false,
      isBulkDeleteDialogOpen: false,
      editingHolidayId: null,
      duplicatingHolidayId: null,
      deletingHolidayId: null,

      // Actions
      setActiveTab: (tab) => set({ activeTab: tab, selectedHolidayIds: [] }),

      setSelectedHolidayIds: (ids) => set({ selectedHolidayIds: ids }),

      toggleHolidaySelection: (id) => {
        const { selectedHolidayIds } = get();
        const newIds = selectedHolidayIds.includes(id)
          ? selectedHolidayIds.filter((selectedId) => selectedId !== id)
          : [...selectedHolidayIds, id];
        set({ selectedHolidayIds: newIds });
      },

      clearSelection: () => set({ selectedHolidayIds: [] }),
      selectAll: (ids) => set({ selectedHolidayIds: [...new Set(ids)] }),
      isAllSelected: (ids) =>
        ids.length > 0 && ids.every((id) => get().selectedHolidayIds.includes(id)),

      // Dialog actions
      openCreateDialog: () =>
        set({
          isCreateDialogOpen: true,
          duplicatingHolidayId: null,
          isEditDialogOpen: false,
          isDeleteDialogOpen: false,
        }),

      openDuplicateDialog: (holidayId) =>
        set({
          isCreateDialogOpen: true,
          duplicatingHolidayId: holidayId,
          isEditDialogOpen: false,
          isDeleteDialogOpen: false,
        }),

      closeCreateDialog: () => set({ isCreateDialogOpen: false, duplicatingHolidayId: null }),

      openEditDialog: (holidayId) =>
        set({
          isEditDialogOpen: true,
          editingHolidayId: holidayId,
          isCreateDialogOpen: false,
          isDeleteDialogOpen: false,
        }),

      closeEditDialog: () =>
        set({
          isEditDialogOpen: false,
          editingHolidayId: null,
        }),

      openDeleteDialog: (holidayId) =>
        set({
          isDeleteDialogOpen: true,
          deletingHolidayId: holidayId,
          isCreateDialogOpen: false,
          isEditDialogOpen: false,
        }),

      closeDeleteDialog: () =>
        set({
          isDeleteDialogOpen: false,
          deletingHolidayId: null,
        }),

      openBulkDeleteDialog: () => set({ isBulkDeleteDialogOpen: true }),
      closeBulkDeleteDialog: () => set({ isBulkDeleteDialogOpen: false }),

      closeAllDialogs: () =>
        set({
          isCreateDialogOpen: false,
          isEditDialogOpen: false,
          isDeleteDialogOpen: false,
          isBulkDeleteDialogOpen: false,
          editingHolidayId: null,
          duplicatingHolidayId: null,
          deletingHolidayId: null,
        }),
    }),
    { name: "holiday-ui-store" }
  )
);
